"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { fetchAuthSession, signIn } from "@aws-amplify/auth";
import { setDocumentCookie, setPgsIdAuthCookie } from "@packages/lib/utlils/cookie-utils";
import { useAuthSession } from "@packages/hooks/useAuthSession";
import { dispatchModalClose } from "@packages/hooks/useIsRegistrationModal";
import emitter, { CustomEvents } from "@packages/lib/eventEmitter/eventEmitter";
import { triggerSigninToast } from "@packages/shared-components/common-utilities/toast/signin-toast";
import SigninContent from "@packages/shared-components/user-profile/signin-signup/signincontent";
import {
  HideEyeIcon,
  ShowEyeIcon,
} from "@packages/shared-components/user-profile/signin-signup/media-utilities/mediautilities";
import { getProjectSpecificData, getCommonAPIHeaders, isPGS } from "@packages/lib/utlils/commonFunction";
import API_URL from "@packages/lib/server-actions/api-urls";
import { logClickstreamEvent } from "@packages/lib/utlils/clickstream";
import {
  CS_EVENT_TYPES,
  CS_SIGNIN_METHODS,
  CS_FUNCTIONALITY,
  CS_ACTION_TYPE,
  CS_NETWORK_ID,
  CS_PGS_NETWORK_ID,
  CS_CTA_TITLES,
} from "@packages/lib/utlils/clickstream-constants";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const MAX_WRONG_PASSWORD_ATTEMPTS = 5;
const ACCOUNT_LOCK_DURATION_MS = 24 * 60 * 60 * 1000;
const PASSWORD_MAX_LENGTH = 128;
const PASSWORD_LIMIT_REACHED_MESSAGE = `Maximum ${PASSWORD_MAX_LENGTH} characters reached`;
const ACCOUNT_LOCKED_MESSAGE =
  "Your account has been temporarily locked due to multiple incorrect password attempts. Please try signing in again in 24 hours";
const EMAIL_MAX_LENGTH = 100;

const checkEmailExists = async (rawEmail: string): Promise<{ action: string; message: string }> => {
  const email = rawEmail.trim().toLowerCase();
  const affiliateId = getProjectSpecificData("affiliateId");
  const apiDomain = process.env.NEXT_PUBLIC_BFF_API_DOMAIN;

  const response = await fetch(`${apiDomain}${API_URL.checkUserExistsHeW}`, {
    method: "POST",
    headers: { ...getCommonAPIHeaders(), Lp: "Y" },
    body: JSON.stringify({ email, affiliateId: String(affiliateId) }),
  });

  if (!response.ok) {
    throw new Error(`Email lookup failed: ${response.status}`);
  }
  const data = await response.json();
  return { action: data?.action || "", message: data?.message || "" };
};

type LeadformSigninModalProps = {
  onClose: () => void;
};

const LeadformSigninModal = ({ onClose }: LeadformSigninModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { refreshSession } = useAuthSession({ redirectOnUnauthenticated: false });

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [formError, setFormError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("signin-remember-me") === "true";
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordShakeTick, setPasswordShakeTick] = useState(0);
  const [emailShakeTick, setEmailShakeTick] = useState(0);
  const [wrongPasswordAttempts, setWrongPasswordAttempts] = useState(0);
  const [lockUntil, setLockUntil] = useState<number | null>(null);
  const [newUserMessage, setNewUserMessage] = useState(""); // shown when user is new, modal stays open
  const closingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Parallel email check state
  const emailCheckResultRef = useRef<{ action: string; message: string } | null>(null); // stores API result
  const emailCheckInFlightRef = useRef<string>(""); // tracks which email is being checked
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);
  const lockStorageKey = useMemo(
    () => `signin-lock:${normalizedEmail || "unknown"}`,
    [normalizedEmail]
  );
  const attemptsStorageKey = useMemo(
    () => `signin-attempts:${normalizedEmail || "unknown"}`,
    [normalizedEmail]
  );
  const isAccountLocked = Boolean(lockUntil && Date.now() < lockUntil);
  const isFormDisabled = isSubmitting || isAccountLocked;

  /** Check email existence on blur — runs in parallel while user types password */
  const handleEmailBlur = useCallback(async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    // Skip if already checked this email
    if (emailCheckResultRef.current !== null && emailCheckInFlightRef.current === trimmed) return;

    emailCheckInFlightRef.current = trimmed;
    emailCheckResultRef.current = null;
    setIsCheckingEmail(true);

    try {
      const result = await checkEmailExists(trimmed);
      // Only store result if email hasn't changed since we started
      if (emailCheckInFlightRef.current === trimmed) {
        emailCheckResultRef.current = result;
      }
    } catch {
      // Silently fail — will retry on submit
      emailCheckResultRef.current = null;
    } finally {
      setIsCheckingEmail(false);
    }
  }, [email]);

  // Load lock state from localStorage
  useEffect(() => {
    if (typeof window === "undefined" || !normalizedEmail || normalizedEmail === "unknown") return;
    const storedAttempts = Number(window.localStorage.getItem(attemptsStorageKey) || "0");
    const storedLockUntil = Number(window.localStorage.getItem(lockStorageKey) || "0");

    setWrongPasswordAttempts(Number.isFinite(storedAttempts) ? storedAttempts : 0);

    if (storedLockUntil && storedLockUntil > Date.now()) {
      setLockUntil(storedLockUntil);
      return;
    }

    setLockUntil(null);
    window.localStorage.removeItem(lockStorageKey);
    if (storedAttempts >= MAX_WRONG_PASSWORD_ATTEMPTS) {
      window.localStorage.setItem(attemptsStorageKey, "0");
      setWrongPasswordAttempts(0);
    }
  }, [attemptsStorageKey, lockStorageKey, normalizedEmail]);

  // Keyboard handling: Escape to close, Tab trap
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();

      if (e.key === "Tab") {
        const modal = modalRef.current;
        if (!modal) return;
        const focusableElements = modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusableElements.length === 0) return;
        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose]
  );

  // Body scroll lock
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
      if (closingTimerRef.current) clearTimeout(closingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Auto-focus first focusable element on mount
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    const firstFocusable = modal.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  }, []);

  // Auto-fill email from "Remember me"
  useEffect(() => {
    const remembered = window.localStorage.getItem("signin-remember-me") === "true";
    const rememberedEmail = window.localStorage.getItem("signin-remembered-email");
    if (remembered && rememberedEmail) {
      setEmail(rememberedEmail);
    }
  }, []);

  const triggerPasswordError = (message: string) => {
    setPasswordError(message);
    setPasswordShakeTick((prev) => prev + 1);
  };

  const triggerEmailError = (message: string) => {
    setEmailError(message);
    setEmailShakeTick((prev) => prev + 1);
  };

  const isValidEmail = (emailVal: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormDisabled) return;

    // Validate email
    if (!normalizedEmail) {
      triggerEmailError("Please enter your email address");
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      triggerEmailError("Please enter a valid email address");
      return;
    }

    // Validate password
    if (!password) {
      triggerPasswordError("Please enter your password");
      return;
    }

    setEmailError("");
    setPasswordError("");
    setFormError("");
    setIsSubmitting(true);

    try {
      // Use pre-fetched email check result if available, otherwise fetch now
      let result = emailCheckResultRef.current;
      if (!result || emailCheckInFlightRef.current !== normalizedEmail) {
        result = await checkEmailExists(normalizedEmail);
      }

      if (result.action === "SOCIAL_ONLY_USER") {
        // Social-only user — block sign-in and show provider message
        triggerEmailError(
          `This email is already registered. Please sign in via ${result.message || "Google or Apple"}`
        );
        setIsSubmitting(false);
        return;
      }

      if (result.action === "PROCEED_TO_SIGN_UP") {
        // User is new — show visible message, then auto-close after delay
        setNewUserMessage("You don't have an account yet. Please register through the form below.");
        setIsSubmitting(false);
        closingTimerRef.current = setTimeout(() => {
          onClose();
        }, 3000);
        return;
      }

      // User exists — attempt sign in
      const signInResult = await signIn({
        username: normalizedEmail,
        password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH",
          clientMetadata: {
            referralLeadProfile: "Y",
          },
        },
      });

      if (signInResult?.nextStep?.signInStep !== "DONE") {
        setFormError("Unable to complete sign in. Please try again.");
        return;
      }

      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString();

      if (!idToken) {
        setFormError("Unable to complete sign in. Please try again.");
        return;
      }

      // Success — clear lock attempts
      if (typeof window !== "undefined") {
        window.localStorage.setItem(attemptsStorageKey, "0");
      }
      setWrongPasswordAttempts(0);

      // Set auth cookie
      
      if (isPGS()) {
       setPgsIdAuthCookie(idToken);
      } else {
        setDocumentCookie({
          name: "wuIdToken",
          value: idToken,
          options: rememberMe ? { maxAge: 60 * 60 * 24 * 30 } : undefined,
        });
      }

      // Handle remember me
      if (typeof window !== "undefined") {
        if (rememberMe) {
          window.localStorage.setItem("signin-remembered-email", normalizedEmail);
        } else {
          window.localStorage.removeItem("signin-remembered-email");
          window.localStorage.removeItem("signin-remember-me");
        }
      }

      // Clickstream: Java parity — fire SignedIn on email sign-in in leadform modal
      logClickstreamEvent({
        eventType: CS_EVENT_TYPES.SIGNED_IN,
        pageName: localStorage?.getItem("gaPageName") || "Register",
        CTATitle: "SIGN IN",
        functionalityName: CS_FUNCTIONALITY.SIGN_IN,
        actionType: CS_ACTION_TYPE,
        signedinMethod: CS_SIGNIN_METHODS.EMAIL,
        networkId: isPGS() ? CS_PGS_NETWORK_ID : CS_NETWORK_ID,
        surveyConsent: "",
        newsletterConsent: "",
        solusConsent: "",
        interestedIntakeYear: "",
        userSegmentation: "",
      });

      // Refresh session, show toast, close modal
      await refreshSession();
      triggerSigninToast("Welcome back");
      dispatchModalClose();
      emitter.emit(CustomEvents.PostAuthRefresh);
      onClose();
    } catch (error: any) {
      const errorName = error?.name || "";

      if (errorName === "NotAuthorizedException") {
        const nextWrongAttempts = wrongPasswordAttempts + 1;
        setWrongPasswordAttempts(nextWrongAttempts);
        if (typeof window !== "undefined") {
          window.localStorage.setItem(attemptsStorageKey, String(nextWrongAttempts));
        }

        if (nextWrongAttempts >= MAX_WRONG_PASSWORD_ATTEMPTS) {
          const nextLockUntil = Date.now() + ACCOUNT_LOCK_DURATION_MS;
          setLockUntil(nextLockUntil);
          setPasswordError("");
          setFormError("");
          if (typeof window !== "undefined") {
            window.localStorage.setItem(lockStorageKey, String(nextLockUntil));
            window.localStorage.setItem(attemptsStorageKey, "0");
          }
          setWrongPasswordAttempts(0);
          return;
        }

        triggerPasswordError("Sorry, email address and password do not match. Please try again or click on Forgot Password ? ");
      } else if (errorName === "UserNotFoundException") {
        // User doesn't exist — show message and auto-close
        setNewUserMessage("You don't have an account yet. Please register through the form below.");
        closingTimerRef.current = setTimeout(() => {
          onClose();
        }, 3000);
      } else if (errorName === "UserNotConfirmedException") {
        setFormError("Your account is not verified. Please check your email for a verification link.");
      } else if (errorName === "PasswordResetRequiredException") {
        setFormError("A password reset is required. Please reset your password to continue.");
      } else if (errorName === "TooManyRequestsException") {
        setFormError("Too many attempts. Please wait a moment and try again.");
      } else {
        setFormError("Sorry, email address and password do not match. Please try again or click on Forgot Password?");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="flex items-start md:items-center justify-center backdrop-shadow-white fixed inset-0 z-50 bg-white"
      role="dialog"
      aria-modal="true"
      aria-label="Sign in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="relative shadow-custom-6 w-full md:w-[728px] bg-white md:rounded-[8px] max-h-[100vh] md:max-h-[96vh] overflow-y-auto md:overflow-x-hidden md:scrollbar-hidden"
      >
        <button
          className="absolute top-[14px] right-[17px] z-[1] cursor-pointer"
          onClick={onClose}
          aria-label="Close sign in modal"
        >
          <Image
            className="block"
            src="/static/assets/icons/modal_close.svg"
            width="14"
            height="14"
            alt="modal close"
          />
        </button>
        <div className="flex flex-col md:flex-row">
          {/* Left panel */}
          <div className="bg-blue-100 px-[16px] py-[40px] md:px-[24px] lg:py-[52px] w-full md:w-[309px] flex-shrink-0 overflow-hidden">
            <SigninContent />
          </div>
          {/* Right panel — Sign in form */}
          <div className="md:m-[40px_20px_0_24px] self-center p-[32px_16px] md:max-h-[537px] md:overflow-y-auto md:overflow-x-hidden md:custom-scrollbar-3 w-full">
              <form className="flex flex-col gap-[24px]" onSubmit={handleSubmit}>
                {/* Heading */}
                <div className="flex flex-col text-center gap-[8px]">
                  <h5>Welcome back!</h5>
                  <div className="small">Enter your password to continue</div>
                </div>

                {/* New user message — shown prominently when user doesn't have an account */}
                {newUserMessage && (
                  <div
                    role="alert"
                    className="small text-center p-[12px] rounded-[4px] text-negative-default bg-red-50 border border-red-200"
                  >
                    {newUserMessage}
                  </div>
                )}

                {/* Form-level error */}
                <div
                  role="alert"
                  className={`small text-center p-[12px] rounded-[4px] ${formError ? "text-negative-default bg-red-50 border border-red-200" : "hidden"}`}
                >
                  {formError || "\u00A0"}
                </div>

                {/* Email field */}
                <div className="flex flex-col basis-full gap-[4px]">
                  <label htmlFor="leadform-signin-email" className="small font-semibold">
                    Email address
                    <span className="text-negative-default">*</span>
                  </label>
                  <input
                    type="email"
                    className={`w-full small font-normal px-[12px] py-[10px] border rounded-[4px] outline-none shadow-custom-2 ${emailError ? `field-invalid-border border-negative-default ${emailShakeTick % 2 === 0 ? "field-invalid-shake-a" : "field-invalid-shake-b"}` : "border-grey-500 focus:border-blue-400"}`}
                    id="leadform-signin-email"
                    maxLength={EMAIL_MAX_LENGTH}
                    value={email}
                    autoComplete="email"
                    aria-invalid={Boolean(emailError)}
                    aria-describedby={emailError ? "leadform-signin-email-error" : undefined}
                    onBlur={handleEmailBlur}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                      setFormError("");
                      // Clear cached result when email changes
                      emailCheckResultRef.current = null;
                    }}
                  />
                  <div
                    id="leadform-signin-email-error"
                    role="alert"
                    className={`x-small min-h-[18px] ${emailError ? "text-negative-default" : "hidden"}`}
                  >
                    {emailError || "\u00A0"}
                  </div>
                </div>

                {/* Password field */}
                <div className="flex flex-col basis-full gap-[4px]">
                  <label htmlFor="leadform-signin-password" className="small font-semibold">
                    Password
                    <span className="text-negative-default">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`w-full small px-[12px] py-[10px] pr-[40px] border rounded-[4px] outline-none shadow-custom-2 placeholder-grey500 ${passwordError ? `field-invalid-border border-negative-default ${passwordShakeTick % 2 === 0 ? "field-invalid-shake-a" : "field-invalid-shake-b"}` : "border-grey-500 focus:border-blue-400"}`}
                      id="leadform-signin-password"
                      placeholder="Please enter your password"
                      maxLength={PASSWORD_MAX_LENGTH}
                      value={password}
                      autoComplete="current-password"
                      aria-invalid={Boolean(passwordError)}
                      aria-describedby={passwordError ? "leadform-signin-password-error" : undefined}
                      disabled={isAccountLocked}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError("");
                      }}
                    />
                    <button
                      className="cursor-pointer absolute inset-y-0 right-[12px] flex items-center text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      type="button"
                      disabled={isAccountLocked}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <ShowEyeIcon /> : <HideEyeIcon />}
                    </button>
                  </div>
                  <div
                    id="leadform-signin-password-error"
                    role="alert"
                    className={`x-small min-h-[18px] ${passwordError ? "text-negative-default" : "hidden"}`}
                  >
                    {passwordError || "\u00A0"}
                  </div>
                  {isAccountLocked && (
                    <div role="alert" className="x-small text-negative-default">
                      {ACCOUNT_LOCKED_MESSAGE}
                    </div>
                  )}
                </div>

                {/* Remember me */}
                <div className="form_check flex flex-col gap-[4px]">
                  <div className="form-check-group flex flex-col gap-[8px]">
                    <div className="flex items-center gap-[12px] relative">
                      <div className="checkbox_card">
                        <input
                          type="checkbox"
                          className="form-checkbox sr-only"
                          id="leadform-rememberMe"
                          checked={rememberMe}
                          disabled={isAccountLocked}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setRememberMe(checked);
                            if (typeof window !== "undefined") {
                              window.localStorage.setItem("signin-remember-me", String(checked));
                              if (!checked) {
                                window.localStorage.removeItem("signin-remembered-email");
                              }
                            }
                          }}
                        />
                        <label
                          htmlFor="leadform-rememberMe"
                          className="flex justify-center items-center w-[16px] h-[16px] rounded-[3px] border border-grey-400 my-[6px] cursor-pointer"
                        >
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M9.2534 0.723569C9.40607 0.863517 9.41638 1.10073 9.27643 1.2534L3.77643 7.2534C3.70732 7.3288 3.6104 7.37269 3.50815 7.37491C3.40589 7.37714 3.30716 7.33749 3.23483 7.26517L0.734835 4.76517C0.588388 4.61872 0.588388 4.38128 0.734835 4.23484C0.881282 4.08839 1.11872 4.08839 1.26517 4.23484L3.48822 6.45789L8.72357 0.746605C8.86351 0.593936 9.10073 0.583622 9.2534 0.723569Z"
                              fill="white"
                              stroke="white"
                              strokeWidth="0.666667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </label>
                      </div>
                      <label htmlFor="leadform-rememberMe" className="check-label small w-[calc(100%_-_28px)]">
                        <span className="x-small text-grey-600">
                          Remember me{" "}
                          <span className="x-small font-normal">
                            Don&apos;t use this on a public computer
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Sign in button */}
                <button
                  type="submit"
                  className="btn btn-primary w-full flex items-center justify-center gap-[8px] disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={isFormDisabled}
                >
                  {isSubmitting ? (
                    <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M4.5 12C4.5 7.875 7.875 4.5 12 4.5C16.125 4.5 19.5 7.875 19.5 12H22C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22V19.5C7.875 19.5 4.5 16.125 4.5 12Z"
                        fill="white"
                      />
                    </svg>
                  ) : (
                    <>
                      Sign in
                      <Image
                        src="/static/assets/icons/right_white_arrow.svg"
                        width={17}
                        height={14}
                        alt="arrow icon"
                      />
                    </>
                  )}
                </button>

                {/* Forgot password */}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!isAccountLocked) {
                      window.open(isPGS() ? "/pgs/forgotpassword" : "/degrees/forgotpassword", "_blank");
                    }
                  }}
                  onKeyDown={(e) => {
                    if (!isAccountLocked && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      window.open(isPGS() ? "/pgs/forgotpassword" : "/degrees/forgotpassword", "_blank");
                    }
                  }}
                  className={`w-fit mx-auto small font-semibold text-center text-primary-400 no-underline transition-colors duration-300 ease-in-out hover:text-primary-500 cursor-pointer ${isAccountLocked ? "pointer-events-none opacity-50" : ""}`}
                >
                  Forgot password?
                </span>
              </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LeadformSigninModal;
